/**
===================================================
Module: frontend.src.services.api.ts
Description: API service for backend communication and authentication
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import {
  User,
  SystemSettings,
  Project,
  Session,
  CreateProjectRequest,
  CreateSessionRequest,
} from "../types/index";

// Import types only - we'll import the service lazily to avoid circular dependency
import type { CognitoAuthResult } from "./cognito";
import type {
  InviteTenantUserRequest,
  InviteTenantUserResponse,
  PageQuota,
  RbacAuditLogResponse,
  Tenant,
  TenantGroup,
  TenantGroupsResponse,
  TenantRole,
  TenantUser,
  TenantUsersResponse,
} from "../types/tenant";

import { getDeviceTrustToken, generateDeviceFingerprint } from "../utils/fingerprint";

export const EXTERNAL_LINKS = {
  HELP_MANUAL: "https://www.kts-download.net/download/ai/arcana-trial_manual.pdf",
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:7890/api";
export const POLLING_INTERVAL_MS = 10000;

/** Carries the HTTP status and backend error code so callers can tell a
 *  permission denial from a missing second factor. Extends Error, so existing
 *  `catch (e) { e.message }` handling is unaffected. */
export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  /** The caller holds the privilege but has not verified this device. */
  get needsOtp(): boolean {
    return this.status === 403 && this.code === "OTP_REQUIRED";
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

/** Where a redacted PDF request stands (GET /sessions/{id}/redacted_pdf/status). */
export interface RedactedPdfStatus {
  status: 'none' | 'queued' | 'running' | 'finalizing' | 'ready' | 'failed';
  content_hash?: string | null;
  /** Seconds since the request was submitted, while it is in progress. */
  elapsed_s?: number;
  /** Why generation failed, when status is 'failed'. */
  reason?: string;
}

// Lazy getter for cognitoService to avoid circular dependency
let _cognitoService: typeof import("./cognito").cognitoService | null = null;
const getCognitoService = async () => {
  if (!_cognitoService) {
    const module = await import("./cognito");
    _cognitoService = module.cognitoService;
  }
  return _cognitoService;
};

class ApiService {
  private async getToken(): Promise<string | null> {
    try {
      // Try to get fresh token from Cognito session
      const cognito = await getCognitoService();
      const token = await cognito.getAccessToken();
      if (token) {
        return token;
      }
    } catch (e) {
      // Cognito not available, fall back to stored token
    }
    // Fallback to stored token
    return localStorage.getItem("access_token");
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = await this.getToken();

    const config: RequestInit = {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(getDeviceTrustToken() && { "X-Device-Trust": getDeviceTrustToken()! }),
        ...options.headers,
      },
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      if (response.status === 401) {
        this.logout();
        window.location.href = "/login";
        throw new Error("Authentication failed. Please login again.");
      }

      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { detail: errorText };
      }
      throw new ApiError(
        errorData.detail || `API Error: ${response.status}`,
        response.status,
        errorData.code,
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Auth endpoints - now using Cognito
  async login(email: string, password: string): Promise<CognitoAuthResult> {
    const cognito = await getCognitoService();
    // Authenticate with Cognito
    const authResult = await cognito.signIn(email, password);

    // Sync user to backend DB
    try {
      await this.syncUser();
    } catch (error) {
      console.warn("User sync failed:", error);
      // Don't fail login if sync fails - user can still use the app
    }

    return authResult;
  }

  async register(
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
    phoneNumber?: string,
    companyName?: string,
    address?: string
  ): Promise<{ message: string; userConfirmed: boolean }> {
    const cognito = await getCognitoService();
    const result = await cognito.signUp(
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      companyName,
      address
    );

    return {
      message: result.userConfirmed
        ? "Registration successful! You can now sign in."
        : "Registration successful! Please check your email for a verification code.",
      userConfirmed: result.userConfirmed,
    };
  }

  async confirmRegistration(email: string, code: string): Promise<void> {
    const cognito = await getCognitoService();
    await cognito.confirmSignUp(email, code);
  }

  async resendVerificationCode(email: string): Promise<void> {
    const cognito = await getCognitoService();
    await cognito.resendConfirmationCode(email);
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const cognito = await getCognitoService();
    await cognito.forgotPassword(email);
    return { message: "Password reset code sent to your email" };
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<{ message: string }> {
    const cognito = await getCognitoService();
    await cognito.confirmForgotPassword(email, code, newPassword);
    return { message: "Password reset successful. You can now sign in." };
  }

  logout(): void {
    // Record logout audit event on the backend
    // Use keepalive: true to ensure the request completes even during page navigation
    const token = localStorage.getItem("access_token") || localStorage.getItem("id_token");
    if (token) {
      fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }).catch(() => { /* best-effort, don't block logout */ });
    }

    // Clear auth tokens (but NOT device_trust_token — it should persist
    // across logout/login cycles for OTP bypass within the trust window)
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("refresh_token");

    // Also sign out from Cognito (async, but we don't wait)
    getCognitoService().then(cognito => cognito.signOut()).catch(() => { });
  }

  /**
   * Decode a JWT payload with proper base64url and UTF-8 handling.
   * JWTs use base64url encoding (RFC 4648) which differs from standard base64:
   * - Uses '-' instead of '+'
   * - Uses '_' instead of '/'
   * - May omit padding '='
   * Additionally, the payload may contain UTF-8 characters (e.g., Japanese names)
   * which atob() doesn't handle correctly.
   */
  private decodeJwtPayload(token: string): Record<string, unknown> {
    const payload = token.split('.')[1];
    if (!payload) {
      throw new Error("Invalid JWT: missing payload");
    }

    // Convert base64url to standard base64
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if needed (base64 requires length to be multiple of 4)
    const paddingNeeded = (4 - (base64.length % 4)) % 4;
    base64 += '='.repeat(paddingNeeded);

    // Decode base64 to binary string, then to UTF-8
    // atob() returns a binary string where each char is a byte (0-255)
    // For UTF-8 content, we need to properly decode the byte sequence
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));
    const jsonString = new TextDecoder('utf-8').decode(bytes);

    return JSON.parse(jsonString);
  }

  /**
   * Sync user from Cognito to backend database
   * Sends user attributes from ID token
   */
  async syncUser(): Promise<User> {
    // Get user attributes from ID token
    const idToken = localStorage.getItem("id_token");
    let userData = {};

    if (idToken) {
      try {
        const decoded = this.decodeJwtPayload(idToken);
        userData = {
          email: decoded.email || "",
          given_name: decoded.given_name || "",
          family_name: decoded.family_name || "",
          phone_number: decoded.phone_number || "",
          email_verified: decoded.email_verified || false,
        };
      } catch (e) {
        console.warn("Failed to decode ID token:", e);
      }
    }

    return this.request<User>("/auth/sync", {
      method: "POST",
      body: JSON.stringify(userData),
    });
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>("/users/me");
  }

  // Admin endpoints
  async getAdminUsers(params: {
    page?: number;
    page_size?: number;
    search?: string;
    company?: string;
    sort?: string;
    sort_dir?: string;
  } = {}): Promise<{ users: User[]; total: number; page: number; page_size: number; company_names: string[] }> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    if (params.search) query.set("search", params.search);
    if (params.company) query.set("company", params.company);
    if (params.sort) query.set("sort", params.sort);
    if (params.sort_dir) query.set("sort_dir", params.sort_dir);
    const qs = query.toString();
    return this.request<{ users: User[]; total: number; page: number; page_size: number; company_names: string[] }>(
      `/admin/users${qs ? `?${qs}` : ""}`
    );
  }

  async createAdminUser(data: any): Promise<User> {
    return this.request<User>('/admin/users', {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAdminUser(userId: string, updates: any): Promise<any> {
    return this.request<any>(`/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteAdminUser(userId: string, permanent: boolean = false): Promise<void> {
    return this.request<void>(`/admin/users/${userId}?permanent=${permanent}`, {
      method: "DELETE",
    });
  }

  /** Complete your own profile after being invited. */
  async updateOwnProfile(data: {
    company_name: string;
    address: string;
    phone_number?: string;
    department?: string;
    first_name?: string;
    last_name?: string;
  }): Promise<User> {
    return this.request<User>("/users/me", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  /** Replace the current password. The server verifies the current one. */
  async changeOwnPassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ message: string; password_change_required: boolean }> {
    return this.request("/users/me/change-password", {
      method: "POST",
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  }

  // Tenant, role and group endpoints
  async getTenant(): Promise<Tenant> {
    return this.request<Tenant>("/tenant");
  }

  async updateTenant(name: string): Promise<{ id: string; name: string }> {
    return this.request<{ id: string; name: string }>("/tenant", {
      method: "PUT",
      body: JSON.stringify({ name }),
    });
  }

  async getTenantUsers(): Promise<TenantUsersResponse> {
    return this.request<TenantUsersResponse>("/tenant/users");
  }

  async inviteTenantUser(
    data: InviteTenantUserRequest,
  ): Promise<InviteTenantUserResponse> {
    return this.request<InviteTenantUserResponse>("/tenant/users", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateTenantUser(
    userId: string,
    updates: {
      tenant_role?: TenantRole;
      group_ids?: string[];
      account_status?: string;
      page_limit?: number;
    },
  ): Promise<{ user: TenantUser; my_quota?: PageQuota }> {
    return this.request<{ user: TenantUser; my_quota?: PageQuota }>(
      `/tenant/users/${userId}`,
      { method: "PUT", body: JSON.stringify(updates) },
    );
  }

  async removeTenantUser(
    userId: string,
  ): Promise<{ message: string; projects_transferred: number }> {
    return this.request<{ message: string; projects_transferred: number }>(
      `/tenant/users/${userId}`,
      { method: "DELETE" },
    );
  }

  async getTenantGroups(): Promise<TenantGroupsResponse> {
    return this.request<TenantGroupsResponse>("/tenant/groups");
  }

  async createTenantGroup(
    name: string,
    description?: string,
  ): Promise<{ group: TenantGroup }> {
    return this.request<{ group: TenantGroup }>("/tenant/groups", {
      method: "POST",
      body: JSON.stringify({ name, description }),
    });
  }

  async updateTenantGroup(
    groupId: string,
    updates: { name?: string; description?: string },
  ): Promise<{ group: TenantGroup }> {
    return this.request<{ group: TenantGroup }>(`/tenant/groups/${groupId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async deleteTenantGroup(groupId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/tenant/groups/${groupId}`, {
      method: "DELETE",
    });
  }

  async addGroupMember(
    groupId: string,
    userId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/tenant/groups/${groupId}/members`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async removeGroupMember(
    groupId: string,
    userId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/tenant/groups/${groupId}/members/${userId}`,
      { method: "DELETE" },
    );
  }

  async moveProjectToGroup(
    projectId: string,
    groupId: string,
  ): Promise<{ project_id: string; group_id: string }> {
    return this.request<{ project_id: string; group_id: string }>(
      `/tenant/projects/${projectId}/group`,
      { method: "PUT", body: JSON.stringify({ group_id: groupId }) },
    );
  }

  async getTenantAuditLogs(
    params: { limit?: number; offset?: number } = {},
  ): Promise<RbacAuditLogResponse> {
    const query = new URLSearchParams();
    if (params.limit !== undefined) query.append("limit", String(params.limit));
    if (params.offset !== undefined) query.append("offset", String(params.offset));
    const qs = query.toString();
    return this.request<RbacAuditLogResponse>(
      `/tenant/audit-logs${qs ? `?${qs}` : ""}`,
    );
  }

  // Project endpoints
  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>("/projects");
  }

  async createProject(project: CreateProjectRequest): Promise<Project> {
    return this.request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async getProject(id: string): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async updateProject(
    id: string,
    project: Partial<CreateProjectRequest>,
  ): Promise<Project> {
    return this.request<Project>(`/projects/${id}`, {
      method: "POST",
      body: JSON.stringify(project),
    });
  }

  async deleteProject(id: string): Promise<void> {
    return this.request<void>(`/projects/${id}`, {
      method: "DELETE",
    });
  }

  // Session endpoints
  async getSessions(projectId: string): Promise<Session[]> {
    return this.request<Session[]>(`/projects/${projectId}/sessions`);
  }

  async getSession(sessionId: string): Promise<Session> {
    return this.request<Session>(`/sessions/${sessionId}`);
  }

  async pollSessions(projectId: string): Promise<Session[]> {
    return this.request<Session[]>(`/projects/${projectId}/sessions`);
  }

  async createSession(
    projectId: string,
    session: CreateSessionRequest,
  ): Promise<{
    id: string;
    upload_id: string;
    part_size: number;
    part_urls: { part_number: number; url: string }[];
    expires_in: number;
    status: string;
  }> {
    return this.request(`/projects/${projectId}/sessions`, {
      method: "POST",
      body: JSON.stringify(session),
    });
  }


  async updateSessionDetails(
    sessionId: string,
    updates: { name: string; description: string },
  ): Promise<Session> {
    return this.request<Session>(`/sessions/${sessionId}/update`, {
      method: "POST",
      body: JSON.stringify(updates),
    });
  }


  /**
   * Upload a file using S3 multipart upload.
   * Splits the file into chunks, uploads each to a presigned URL,
   * and returns the list of {PartNumber, ETag} for completion.
   */
  async uploadPdfFile(
    file: File,
    partSize: number,
    partUrls: { part_number: number; url: string }[],
    sessionId?: string,
    uploadId?: string,
    onProgress?: (completed: number, total: number) => void,
  ): Promise<{ PartNumber: number; ETag: string }[]> {
    const MAX_CONCURRENT = 6;
    const MAX_RETRIES = 4;
    const parts: { PartNumber: number; ETag: string }[] = [];

    // Parts S3 already holds from an earlier attempt are not re-sent. Only
    // possible when we know which upload to ask about.
    let alreadyUploaded = new Map<number, string>();
    if (sessionId && uploadId) {
      try {
        const existing = await this.listUploadedParts(sessionId, uploadId);
        for (const p of existing) {
          // A part is only reusable if it is a full-size part, or the final
          // one; a short part means the previous attempt was interrupted
          // mid-write for that index.
          const start = (p.PartNumber - 1) * partSize;
          const expected = Math.min(partSize, file.size - start);
          if (p.Size === expected) {
            alreadyUploaded.set(p.PartNumber, p.ETag);
          }
        }
        if (alreadyUploaded.size > 0) {
          console.info(
            `Resuming upload: ${alreadyUploaded.size}/${partUrls.length} parts already stored`,
          );
        }
      } catch (err) {
        // Resume is an optimization; fall back to uploading everything.
        console.warn("Could not list existing parts, uploading all:", err);
      }
    }

    // Upload a single part using XMLHttpRequest for reliability
    const uploadPart = (
      partNumber: number,
      url: string,
      blob: Blob,
    ): Promise<{ PartNumber: number; ETag: string }> => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url, true);
        xhr.timeout = 0; // no timeout for large chunks

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader("ETag");
            if (!etag) {
              reject(new Error(`No ETag in response for part ${partNumber}`));
              return;
            }
            resolve({ PartNumber: partNumber, ETag: etag });
          } else {
            reject(
              new Error(
                `Part ${partNumber} upload failed: ${xhr.status} ${xhr.statusText}`,
              ),
            );
          }
        };

        xhr.onerror = () =>
          reject(new Error(`Part ${partNumber} upload failed: network error`));
        xhr.send(blob);
      });
    };

    // Upload with retry. Every retry after the first asks the backend for a
    // fresh presigned URL: the URLs issued at session creation all expire on
    // one clock started before the upload began, and retrying an expired URL
    // fails identically every time.
    const uploadPartWithRetry = async (
      partNumber: number,
      url: string,
      blob: Blob,
    ): Promise<{ PartNumber: number; ETag: string }> => {
      let lastError: Error | undefined;
      let currentUrl = url;

      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          return await uploadPart(partNumber, currentUrl, blob);
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          console.warn(
            `Part ${partNumber} attempt ${attempt}/${MAX_RETRIES} failed:`,
            lastError.message,
          );
          if (attempt < MAX_RETRIES) {
            // Exponential backoff: 1s, 2s, 4s, 8s
            await new Promise((r) =>
              setTimeout(r, Math.pow(2, attempt - 1) * 1000),
            );
            if (sessionId && uploadId) {
              try {
                currentUrl = await this.getPartUploadUrl(
                  sessionId,
                  uploadId,
                  partNumber,
                );
              } catch (urlErr) {
                console.warn(
                  `Could not refresh URL for part ${partNumber}:`,
                  urlErr,
                );
              }
            }
          }
        }
      }
      throw lastError;
    };

    // Worker pool: each slot takes the next pending part as soon as it frees
    // up. A fixed batch would stall on its slowest part before starting the
    // next group.
    const pending = partUrls.filter(
      ({ part_number }) => !alreadyUploaded.has(part_number),
    );
    alreadyUploaded.forEach((etag, partNumber) => {
      parts.push({ PartNumber: partNumber, ETag: etag });
    });

    let cursor = 0;
    let completed = alreadyUploaded.size;
    const total = partUrls.length;
    onProgress?.(completed, total);

    const worker = async (): Promise<void> => {
      while (cursor < pending.length) {
        const { part_number, url } = pending[cursor++];
        const start = (part_number - 1) * partSize;
        const end = Math.min(start + partSize, file.size);
        const result = await uploadPartWithRetry(
          part_number,
          url,
          file.slice(start, end),
        );
        parts.push(result);
        completed += 1;
        onProgress?.(completed, total);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(MAX_CONCURRENT, pending.length) }, worker),
    );

    return parts.sort((a, b) => a.PartNumber - b.PartNumber);
  }

  /** Get a fresh presigned URL for a single part of an in-progress upload. */
  async getPartUploadUrl(
    sessionId: string,
    uploadId: string,
    partNumber: number,
  ): Promise<string> {
    const res = await this.request<{ url: string }>(
      `/sessions/${sessionId}/upload/part-url`,
      {
        method: "POST",
        body: JSON.stringify({ upload_id: uploadId, part_number: partNumber }),
      },
    );
    return res.url;
  }

  /** List parts S3 already holds, so an interrupted upload can resume. */
  async listUploadedParts(
    sessionId: string,
    uploadId: string,
  ): Promise<{ PartNumber: number; ETag: string; Size: number }[]> {
    const res = await this.request<{
      parts: { PartNumber: number; ETag: string; Size: number }[];
    }>(
      `/sessions/${sessionId}/upload/parts?upload_id=${encodeURIComponent(uploadId)}`,
      { method: "GET" },
    );
    return res.parts || [];
  }

  async completeMultipartUpload(
    sessionId: string,
    uploadId: string,
    parts: { PartNumber: number; ETag: string }[],
  ): Promise<void> {
    return this.request<void>(`/sessions/${sessionId}/upload/complete`, {
      method: "POST",
      body: JSON.stringify({ upload_id: uploadId, parts }),
    });
  }

  async abortMultipartUpload(
    sessionId: string,
    uploadId: string,
  ): Promise<void> {
    return this.request<void>(`/sessions/${sessionId}/upload/abort`, {
      method: "POST",
      body: JSON.stringify({ upload_id: uploadId }),
    });
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem("access_token");
  }

  // OTP endpoints
  async sendOtp(): Promise<{ message: string; masked_email: string; expires_in_seconds: number; already_trusted?: boolean }> {
    const fingerprint = await generateDeviceFingerprint();
    return this.request("/auth/otp/send", {
      method: "POST",
      headers: { "X-Device-Fingerprint": fingerprint },
    });
  }

  async verifyOtp(code: string): Promise<{ verified: boolean; trust_token: string; trust_expires_at: string; trust_window_hours: number }> {
    const fingerprint = await generateDeviceFingerprint();
    return this.request("/auth/otp/verify", {
      method: "POST",
      body: JSON.stringify({ code, device_fingerprint: fingerprint }),
      headers: { "X-Device-Fingerprint": fingerprint },
    });
  }

  async getOtpStatus(): Promise<{ otp_required: boolean; device_trusted: boolean }> {
    return this.request("/auth/otp/status");
  }

  // Audit log endpoints
  async getAuditLogs(params: {
    page?: number;
    page_size?: number;
    from?: string;
    to?: string;
  } = {}): Promise<{
    logs: Array<{
      id: number;
      event_type: string;
      event_time: string;
      ip_address: string;
      user_agent: string;
      device_info: string;
      session_id: string;
      metadata: Record<string, unknown>;
    }>;
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const query = new URLSearchParams();
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    const qs = query.toString();
    return this.request(`/users/me/audit-logs${qs ? `?${qs}` : ""}`);
  }

  async exportAuditLogs(format: string = "csv", from?: string, to?: string): Promise<void> {
    const token = await this.getToken();
    const query = new URLSearchParams({ format });
    if (from) query.set("from", from);
    if (to) query.set("to", to);

    const response = await fetch(`${API_BASE_URL}/users/me/audit-logs/export?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(getDeviceTrustToken() && { "X-Device-Trust": getDeviceTrustToken()! }),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to export audit logs");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_log.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async getAdminAuditLogs(params: {
    user_id?: string;
    page?: number;
    page_size?: number;
    from?: string;
    to?: string;
  } = {}): Promise<{
    logs: Array<{
      id: number;
      user_id: string;
      user_email: string;
      event_type: string;
      event_time: string;
      ip_address: string;
      device_info: string;
    }>;
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const query = new URLSearchParams();
    if (params.user_id) query.set("user_id", params.user_id);
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    const qs = query.toString();
    return this.request(`/admin/audit-logs${qs ? `?${qs}` : ""}`);
  }

  async deleteSessions(sessionIds: string[]): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/sessions/bulk-delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ session_ids: sessionIds }),
    });

    if (!response.ok) {
      throw new Error("Failed to delete sessions");
    }
  }

  // PDF Editor endpoints
  async getSessionInputPdfBlob(sessionId: string): Promise<Blob> {
    // Step 1: Get presigned URL from backend
    const data = await this.request<{ url: string; filename: string }>(`/sessions/${sessionId}/input_pdf`);

    // Step 2: Fetch PDF directly from S3 using presigned URL
    const response = await fetch(data.url);
    if (!response.ok) {
      throw new Error(`Failed to load PDF: ${response.statusText}`);
    }
    return response.blob();
  }

  async getSessionRedactionBoxes(sessionId: string): Promise<any[]> {
    // Step 1: Get presigned URL from backend
    const data = await this.request<{ url: string }>(`/sessions/${sessionId}/redaction_boxes`);

    // Step 2: Fetch JSON directly from S3 using presigned URL
    const response = await fetch(data.url);
    if (!response.ok) {
      throw new Error(`Failed to load redaction boxes: ${response.statusText}`);
    }
    return response.json();
  }

  async exportRedactionList(sessionId: string, docName?: string): Promise<void> {
    const token = await this.getToken();

    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/redaction-list/export?format=csv`, {
      headers: {
        Authorization: `Bearer ${token}`,
        ...(getDeviceTrustToken() && { "X-Device-Trust": getDeviceTrustToken()! }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || "Failed to export redaction list");
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Extract filename from Content-Disposition header, fallback to dynamic default
    // Naming spec (issue #8): <uploaded pdf name>_黒塗りリスト.csv
    const contentDisposition = response.headers.get("Content-Disposition") || response.headers.get("content-disposition");
    const baseName = docName ? docName.replace(/\.[^/.]+$/, "") : "document";
    let filename = `${baseName}_黒塗りリスト.csv`;

    if (contentDisposition) {
      const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
      if (filenameStarMatch) {
        try {
          filename = decodeURIComponent(filenameStarMatch[1]);
        } catch {
          filename = filenameStarMatch[1];
        }
      } else if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Request a redacted PDF. Idempotent per content: if a PDF for exactly
   * these boxes is already built it comes back 'ready', and if one is being
   * built that job is reused. Only changed boxes start a new job.
   */
  async applyRedactions(
    sessionId: string,
    redactionBoxes: any,
    mode: 'black' | 'transparent' = 'black',
  ): Promise<RedactedPdfStatus> {
    return this.request<RedactedPdfStatus>('/apply-redactions', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        redaction_boxes: redactionBoxes,
        mode,
      }),
    });
  }

  /** Where the latest redacted PDF request for this session and mode stands. */
  async getRedactedPdfStatus(
    sessionId: string,
    mode: 'black' | 'transparent' = 'black',
  ): Promise<RedactedPdfStatus> {
    return this.request<RedactedPdfStatus>(
      `/sessions/${sessionId}/redacted_pdf/status?mode=${mode}`,
      { method: 'GET' },
    );
  }

  async persistRedactions(sessionId: string, redactionBoxes: any[]): Promise<void> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/persist-redactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        session_id: sessionId,
        redaction_boxes: redactionBoxes
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to persist redactions: ${errorText}`);
    }
  }

  async getSessionInputPdfUrl(sessionId: string): Promise<string> {
    // Get presigned URL from backend - can be used directly for PDF viewing
    const data = await this.request<{ url: string; filename: string }>(`/sessions/${sessionId}/input_pdf`);
    return data.url;
  }

  async checkRedactedPdfExists(sessionId: string, mode: 'black' | 'transparent' = 'black'): Promise<boolean> {
    const token = await this.getToken();
    const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/redacted_pdf?mode=${mode}`, {
      method: 'HEAD',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.ok;
  }

  /**
   * Save the redacted PDF through the browser's own download manager.
   *
   * The presigned URL already carries the Content-Disposition filename, so
   * navigating to it downloads under the right name, streams straight to
   * disk, shows the browser's progress bar and can resume. Fetching it into a
   * Blob first held the entire PDF in memory (1.5 GB for a 3,000-page
   * document) behind a static notification. The server records the download
   * when it issues the link, so no completion callback is needed.
   */
  async downloadRedactedPdf(sessionId: string, mode: 'black' | 'transparent' = 'black'): Promise<void> {
    const data = await this.request<{ download_url: string; filename?: string }>(
      `/sessions/${sessionId}/redacted_pdf?mode=${mode}`,
      { method: 'GET' },
    );

    const a = document.createElement('a');
    a.href = data.download_url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async updateUser(userId: string, data: Partial<User>): Promise<void> {
    return this.request<void>(`/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getSettings(): Promise<SystemSettings> {
    return this.request<SystemSettings>("/admin/settings");
  }

  async updateSettings(settings: Partial<SystemSettings>): Promise<void> {
    return this.request<void>("/admin/settings", {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  // ===== System Notifications =====

  async getNotifications(): Promise<{ notifications: any[] }> {
    return this.request<{ notifications: any[] }>("/notifications");
  }

  async getAdminNotifications(page = 1, pageSize = 20): Promise<any> {
    return this.request<any>(`/admin/notifications?page=${page}&page_size=${pageSize}`);
  }

  async createNotification(data: {
    title: string;
    content: string;
    severity: string;
    display_start: string;
    display_end: string;
  }): Promise<any> {
    return this.request<any>("/admin/notifications", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateNotification(id: number, data: Record<string, any>): Promise<any> {
    return this.request<any>(`/admin/notifications/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteNotification(id: number): Promise<any> {
    return this.request<any>(`/admin/notifications/${id}`, {
      method: "DELETE",
    });
  }
}

export const apiService = new ApiService();
